import { useState } from 'react';
import { useReportStore } from '../../stores/reportStore';
import { usePreferencesStore } from '../../stores/preferencesStore';
import { Loader2, Code, Sparkles, ChevronDown, ChevronUp, Terminal, MessageSquare } from 'lucide-react';

export default function DebugView() {
  const {
    rawReport,
    generatedReport,
    debugPrompt,
    debugUserMessage,
    isGenerating,
  } = useReportStore();

  const { preferences } = usePreferencesStore();
  const [showSystemPrompt, setShowSystemPrompt] = useState(true);
  const [showUserMessage, setShowUserMessage] = useState(true);

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
          Debug View - API Input/Output
        </h2>
        {isGenerating && (
          <div className="flex items-center gap-2 text-sm text-primary-600">
            <Loader2 size={16} className="animate-spin" />
            Generating...
          </div>
        )}
      </div>

      {/* Prompts Section - Collapsible */}
      <div className="mb-4 space-y-2 flex-shrink-0">
        {/* System Prompt */}
        <div className="border border-orange-300 dark:border-orange-700 rounded-lg overflow-hidden">
          <button
            onClick={() => setShowSystemPrompt(!showSystemPrompt)}
            className="w-full flex items-center justify-between px-3 py-2 bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300 text-sm font-medium"
          >
            <div className="flex items-center gap-2">
              <Terminal size={16} />
              SYSTEM PROMPT (sent to OpenAI)
            </div>
            {showSystemPrompt ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
          {showSystemPrompt && (
            <div className="p-3 bg-orange-50 dark:bg-slate-800 max-h-48 overflow-y-auto">
              <pre className="text-xs text-gray-700 dark:text-gray-300 whitespace-pre-wrap font-mono">
                {debugPrompt || 'No system prompt captured yet. Generate a report to see the prompt.'}
              </pre>
            </div>
          )}
        </div>

        {/* User Message */}
        <div className="border border-purple-300 dark:border-purple-700 rounded-lg overflow-hidden">
          <button
            onClick={() => setShowUserMessage(!showUserMessage)}
            className="w-full flex items-center justify-between px-3 py-2 bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300 text-sm font-medium"
          >
            <div className="flex items-center gap-2">
              <MessageSquare size={16} />
              USER MESSAGE (sent to OpenAI)
            </div>
            {showUserMessage ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
          {showUserMessage && (
            <div className="p-3 bg-purple-50 dark:bg-slate-800 max-h-32 overflow-y-auto">
              <pre className="text-xs text-gray-700 dark:text-gray-300 whitespace-pre-wrap font-mono">
                {debugUserMessage || 'No user message captured yet. Generate a report to see the message.'}
              </pre>
            </div>
          )}
        </div>
      </div>

      {/* Two-pane layout */}
      <div className="flex-1 flex gap-4 overflow-hidden min-h-0">
        {/* Left Pane - Raw LLM Output */}
        <div className="flex-1 flex flex-col overflow-hidden">
          <div className="flex items-center gap-2 mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
            <Code size={16} className="text-blue-500" />
            Raw GPT Response
          </div>
          <div
            className="flex-1 overflow-y-auto bg-blue-50 dark:bg-slate-700 rounded-lg border-2 border-blue-200 dark:border-blue-800 p-4"
            style={{ fontSize: `${preferences?.font_size || 14}px` }}
          >
            {rawReport ? (
              <pre
                className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap font-mono"
              >
                {rawReport}
              </pre>
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-gray-400 dark:text-gray-500">
                <Code size={32} className="mb-2 opacity-50" />
                <p className="text-center text-sm">
                  Raw LLM output will appear here
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Right Pane - Processed Output */}
        <div className="flex-1 flex flex-col overflow-hidden">
          <div className="flex items-center gap-2 mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
            <Sparkles size={16} className="text-emerald-500" />
            After Validation Engine
          </div>
          <div
            className="flex-1 overflow-y-auto bg-emerald-50 dark:bg-slate-700 rounded-lg border-2 border-emerald-200 dark:border-emerald-800 p-4"
            style={{ fontSize: `${preferences?.font_size || 14}px` }}
          >
            {generatedReport ? (
              <div
                className="prose prose-sm dark:prose-invert max-w-none"
                dangerouslySetInnerHTML={{ __html: generatedReport }}
              />
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-gray-400 dark:text-gray-500">
                <Sparkles size={32} className="mb-2 opacity-50" />
                <p className="text-center text-sm">
                  Processed output will appear here
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="mt-3 flex items-center justify-center gap-6 text-xs text-gray-500 dark:text-gray-400 flex-shrink-0">
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded bg-orange-200 dark:bg-orange-800" />
          <span>System prompt</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded bg-purple-200 dark:bg-purple-800" />
          <span>User message</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded bg-blue-200 dark:bg-blue-800" />
          <span>Raw response</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded bg-emerald-200 dark:bg-emerald-800" />
          <span>After validation</span>
        </div>
      </div>
    </div>
  );
}
